import { ReportGenerateDataRequest } from '../_models/reports/report-generate-data-request.model';
import { ReportGenerateData } from '../_models/reports/report-generate-data.model';
import { ClientReportBatchRequest } from '../_models/reports/client-report-batch-request.model';
import { buildClientReportFilter } from './client-report-filter.util';

/**
 * The Generate flow's request building, shared by the Generate tab and the combined Reports page: which booking lines a
 * report would go out for, and the batch that sends them. The form value is the Generate form's shape (startDate,
 * endDate, reports, clients, tutors, students, brands); the combined page passes one client at a time.
 */

/** A form date as the API expects it: the same calendar day at UTC midnight. */
export function toRequestDate(date: any): Date {
  const d = new Date(date);
  return new Date(d.setMinutes(d.getMinutes() - d.getTimezoneOffset()));
}

/** Who is asking, for the list request: their user id, email and client-user rows (referral-only access). */
export interface SendRequester {
  userId: number;
  email: string;
  clientUsers: { clientId: any, viewReferrals?: boolean }[];
}

/**
 * The request for the booking lines a report would be sent for, and the learner ids picked (several may be; the caller
 * filters the lines to them when there is more than one).
 */
export function buildSendListRequest(formValue: any, clientId: any, companyIds: number[],
  requester: SendRequester): { params: ReportGenerateDataRequest, studentIds: number[] } {
  const params = new ReportGenerateDataRequest();
  params.startDate = toRequestDate(formValue.startDate);
  params.endDate = toRequestDate(formValue.endDate);
  params.createdBy = requester.userId;
  params.reportTypeId = formValue.reports.reportId;
  params.companyIds = companyIds;

  if (clientId != null) {
    params.clientId = clientId.toString();
    const clientUser = (requester.clientUsers ?? []).find(x => x.clientId === clientId);
    if (clientUser != null && clientUser.viewReferrals) {
      params.referrerEmail = requester.email;
    }
  }

  let studentIds: number[] = [];
  const studentValue = formValue.students;
  if (studentValue != null) {
    studentIds = Array.isArray(studentValue) ? studentValue : [studentValue];
    if (studentIds.length === 1) {
      // A single learner: the list endpoint filters on it.
      params.studentId = studentIds[0];
    }
  }
  if (formValue.tutors != null) {
    params.tutorId = formValue.tutors;
  }
  return { params, studentIds };
}

/** The lines returned for a list request, narrowed to the learners picked when there are several. */
export function linesForLearners(lines: ReportGenerateData[], studentIds: number[]): ReportGenerateData[] {
  return studentIds.length > 1 ? lines.filter(d => studentIds.indexOf(d.studentId) >= 0) : lines;
}

/** The lines a send goes out for by default: those whose report is required. */
export function requiredLines(lines: ReportGenerateData[]): ReportGenerateData[] {
  return lines.filter(d => (d.reportRequired ?? '').toLowerCase() === 'yes');
}

/** A native report's batch: one report code, the filter, the lines, and PDF when asked for. */
export function buildNativeBatchRequest(formValue: any, nativeCode: string, studentIds: number[],
  lines: ReportGenerateData[], pdf: boolean): ClientReportBatchRequest {
  const request = new ClientReportBatchRequest();
  request.reportFileName = formValue.reports.name;
  request.reports = [nativeCode];
  request.filter = buildClientReportFilter(formValue, studentIds);
  request.reportRequests = lines;
  if (pdf) {
    request.format = 'pdf';
  }
  return request;
}
